// src/App.js

import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

// Custom axios instance with error handling
import axios from "./axiosConfig";
import api from "./services/api";

// Analytics tracking
import { trackPageView } from "./utils/analytics";
import { trackExternalPageView } from "./utils/trackingScripts";

// Error Boundary
import ErrorBoundary from "./components/ErrorBoundary";

// Layouts
import HomeLayout from "./layouts/HomeLayout";
import PublicLayout from "./layouts/PublicLayout";
import PrivateLayout from "./layouts/PrivateLayout";
import AuthLayout from "./layouts/AuthLayout";

// Other utilities
import PrivateRoute from "./components/PrivateRoute";
import AuthRedirect from "./components/AuthRedirect";
import RoleRoute from "./components/RoleRoute";
import { STAFF_ROLES, ROLE_ADMIN, ADMIN_OR_MANAGER_ROLES } from "./utils/userRoles";
import CookieConsent from "./components/CookieConsent";
import TrackingScripts from "./components/TrackingScripts";

// Public pages
import Home from "./components/Home";
import AboutUs from "./components/AboutUs";
import Login from "./components/Login";
import Register from "./components/Register";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import PrivacyPolicy from "./PrivacyPolicy";
import PaymentSuccess from "./components/PaymentSuccess";
import Parsing from "./components/Parsing";
import ParsingDetail from "./components/ParsingDetail";
import Telegram from "./components/Telegram";
import TelegramParsingDetail from "./components/TelegramParsingDetail";
import ErrorPage from "./components/ErrorPage";
import ErrorTest from "./components/ErrorTest";
import ErrorDebug from "./components/ErrorDebug";
import TermsOfService from "./components/TermsOfService";
import SmsUnsubscribe from "./components/SmsUnsubscribe";
import EmbedForm from "./components/EmbedForm";
import SignupConfirm from "./components/SignupConfirm";
import SubscribeForms from "./components/SubscribeForms";

// Private pages
import Campaigns from "./components/Campaigns";
import Requests from "./components/Requests";
import ContactList from "./components/ContactList";
import Dashboard from "./components/Dashboard";
import CampaignDashboard from "./components/CampaignDashboard";
import Emails from "./components/Emails";
import Domains from "./components/Domains";
import Template from "./components/Template";
import SMSDashboard from "./components/SMSDashboard";
import SMSCampaign from "./components/SMSCampaign";
import SMSContacts from "./components/SMSContacts";
import SMSPhones from "./components/SMSPhones";
import AdminDashboard from "./components/AdminDashboard";
import Pricing from "./components/Pricing";
import MyProfile from "./components/MyProfile";
import Billing from "./components/Billing";
import Notifications from "./components/Notifications";
import { SnackbarProvider } from "notistack";
import CustomNotificationContent from "./components/CustomNotificationContent";
import Messages from "./components/Messages";
import CustomMailNotificationContent from "./components/CustomMailNotificationContent";
import { SocketProvider } from "./SocketContext";
import MailsballEditorPro from "./components/MailsballEditorPro";
import AdminMobileMonitoring from "./components/AdminMobileMonitoring";
import Settings from "./components/Settings";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { FEATURES } from "./config/features";

// Создаем компонент-обертку для инициализации API с навигацией
const AppWithNavigation = () => {
  const navigate = useNavigate();

  // Инициализируем API с функцией навигации
  useEffect(() => {
    api.initApi(navigate);
  }, [navigate]);

  return <AppContent />;
};

// Основное содержимое приложения
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [, setOriginalUser] = useState(null);

  // Track page views when route changes
  useEffect(() => {
    if (!loadingAuth && !location.pathname.startsWith("/u/") && !location.pathname.startsWith("/embed/") && !location.pathname.startsWith("/signup/confirm/")) {
      // Track page view with current path and user ID if available
      trackPageView(location.pathname, user?._id);
      trackExternalPageView(location.pathname);

      // Log for debugging
      console.log(`[App] Page view tracked for: ${location.pathname}`);
    }
  }, [location.pathname, user, loadingAuth]);

  // Check if user is authenticated
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        console.log("Checking authentication status...");
        const res = await axios
          .get("/api/auth/check-session")
          .then((response) => response.data);

        console.log("Auth check response:", res);

        if (res && res.authenticated && res.user) {
          console.log("User is authenticated:", res.user.id);
          setAuthenticated(true);
          setUser(res.user);
          setIsImpersonating(res.isImpersonating || false);
          setOriginalUser(res.originalUser || null);
        } else {
          console.log("User is not authenticated");
          setAuthenticated(false);
          setUser(null);
          setIsImpersonating(false);
          setOriginalUser(null);
        }
      } catch (err) {
        console.error("Failed to check session:", err);
        setAuthenticated(false);
        setUser(null);
        setIsImpersonating(false);
        setOriginalUser(null);
      } finally {
        setLoadingAuth(false);
      }
    };

    checkAuthStatus();

    // Периодическая проверка сессии каждые 5 минут
    const sessionCheckInterval = setInterval(
      () => {
        if (authenticated) {
          // Только если пользователь уже аутентифицирован
          console.log("Performing periodic session check...");
          checkAuthStatus();
        }
      },
      5 * 60 * 1000,
    ); // 5 минут

    return () => clearInterval(sessionCheckInterval);
  }, [authenticated]); // Добавляем 'authenticated' в зависимости, чтобы сбрасывать интервал при выходе

  const handleLogout = () => {
    axios
      .post("/api/auth/logout", {})
      .then(() => {
        setAuthenticated(false);
        // Сначала устанавливаем, что пользователь вышел из системы
        setUser(null);
        setIsImpersonating(false);
        setOriginalUser(null);

        // Перенаправляем на главную страницу
        navigate("/");

        // Добавляем небольшую задержку и выполняем полное обновление страницы
        setTimeout(() => {
          window.location.reload();
        }, 100);
      })
      .catch((err) => {
        console.error("Logout error:", err);
        // При ошибке тоже делаем полное обновление страницы
        window.location.reload();
      });
  };

  const handleUserChange = (newUser) => {
    setUser(newUser);
    // Перезагружаем состояние аутентификации
    const checkAuthStatus = async () => {
      try {
        const res = await axios
          .get("/api/auth/check-session")
          .then((response) => response.data);

        if (res && res.authenticated && res.user) {
          setAuthenticated(true);
          setUser(res.user);
          setIsImpersonating(res.isImpersonating || false);
          setOriginalUser(res.originalUser || null);
        }
      } catch (err) {
        console.error("Failed to check session after user change:", err);
      }
    };

    checkAuthStatus();
  };

  if (loadingAuth) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        Components={{
          customNotification: CustomNotificationContent,
          emailNotification: CustomMailNotificationContent,
        }}
      >
        <SocketProvider userId={user?._id}>
          <CookieConsent />
          <TrackingScripts />
          {/* Баннер для режима импersonation */}
          {isImpersonating && (
            <ImpersonationBanner
              currentUser={user}
              onUserChange={handleUserChange}
            />
          )}
          <Routes>
            <Route path="/u/:code" element={<SmsUnsubscribe />} />
            <Route path="/embed/:publicId" element={<EmbedForm />} />
            <Route path="/signup/confirm/:token" element={<SignupConfirm />} />

            {/* HOME LAYOUT */}
            <Route
              path="/"
              element={
                <HomeLayout
                  authenticated={authenticated}
                  user={user}
                  handleLogout={handleLogout}
                  isImpersonating={isImpersonating}
                />
              }
            >
              <Route index element={<Home />} />
              <Route path="/about" element={<AboutUs />} />
            </Route>

            {/* AUTH LAYOUT — full screen, no nav/footer */}
            <Route element={<AuthLayout />}>
              <Route
                path="/login"
                element={
                  <AuthRedirect isAuthenticated={authenticated}>
                    <Login onLoginSuccess={() => setAuthenticated(true)} />
                  </AuthRedirect>
                }
              />
              <Route
                path="/register"
                element={
                  <AuthRedirect isAuthenticated={authenticated}>
                    <Register />
                  </AuthRedirect>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <AuthRedirect isAuthenticated={authenticated}>
                    <ForgotPassword />
                  </AuthRedirect>
                }
              />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>

            {/* PUBLIC LAYOUT */}
            <Route
              element={
                <PublicLayout
                  authenticated={authenticated}
                  user={user}
                  handleLogout={handleLogout}
                  isImpersonating={isImpersonating}
                />
              }
            >
              <Route path="/success" element={<PaymentSuccess />} />
              <Route path="/error-test" element={<ErrorTest />} />
              <Route path="/error-debug" element={<ErrorDebug />} />

              <Route
                path="/error/404"
                element={<ErrorPage statusCode={404} />}
              />
              <Route
                path="/error/500"
                element={<ErrorPage statusCode={500} />}
              />
              <Route
                path="/maintenance"
                element={<ErrorPage statusCode="maintenance" />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
            </Route>

            {/* /pricing: ???? PrivateLayout, ???? PublicLayout */}
            <Route
              path="/pricing"
              element={
                authenticated ? (
                  <PrivateLayout
                    authenticated={authenticated}
                    user={user}
                    setAppUser={setUser}
                    handleLogout={handleLogout}
                    isImpersonating={isImpersonating}
                    onUserChange={handleUserChange}
                  />
                ) : (
                  <PublicLayout
                    authenticated={authenticated}
                    user={user}
                    handleLogout={handleLogout}
                    isImpersonating={isImpersonating}
                    onUserChange={handleUserChange}
                  />
                )
              }
            >
              <Route path="/pricing" element={<Pricing />} />
            </Route>

            {/* /parsing/*: Admin and Manager only */}
            <Route
              path="/parsing/*"
              element={
                <PrivateRoute
                  isAuthenticated={authenticated}
                  element={
                    <PrivateLayout
                      authenticated={authenticated}
                      user={user}
                      setAppUser={setUser}
                      handleLogout={handleLogout}
                      isImpersonating={isImpersonating}
                      onUserChange={handleUserChange}
                    />
                  }
                />
              }
            >
              <Route
                index
                element={
                  <RoleRoute user={user} allowedRoles={[...ADMIN_OR_MANAGER_ROLES]}>
                    <Parsing />
                  </RoleRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <RoleRoute user={user} allowedRoles={[...ADMIN_OR_MANAGER_ROLES]}>
                    <ParsingDetail />
                  </RoleRoute>
                }
              />
              <Route
                path="telegram"
                element={
                  <RoleRoute user={user} allowedRoles={[ROLE_ADMIN]}>
                    <Telegram />
                  </RoleRoute>
                }
              />
              <Route
                path="telegram/:id"
                element={
                  <RoleRoute user={user} allowedRoles={[ROLE_ADMIN]}>
                    <TelegramParsingDetail />
                  </RoleRoute>
                }
              />
            </Route>

            {/* PRIVATE LAYOUT (??? ?????????? ???????) */}
            <Route
              element={
                <PrivateLayout
                  authenticated={authenticated}
                  user={user}
                  setAppUser={setUser}
                  handleLogout={handleLogout}
                  isImpersonating={isImpersonating}
                  onUserChange={handleUserChange}
                />
              }
            >
              <Route
                path="/campaigns"
                element={
                  <PrivateRoute
                    element={<Campaigns />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/notifications"
                element={
                  <PrivateRoute
                    element={<Notifications />} // ?????? <Notifications />
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/requests"
                element={
                  <PrivateRoute
                    element={
                      FEATURES.PROJECTS_ENABLED ? (
                        <Requests />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    }
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/subscribe-forms"
                element={
                  <PrivateRoute
                    element={<SubscribeForms />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route path="/signup-forms" element={<Navigate to="/subscribe-forms" replace />} />
              <Route
                path="/contacts"
                element={
                  <PrivateRoute
                    element={<ContactList />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/contacts/:groupName"
                element={
                  <PrivateRoute
                    element={<ContactList />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute
                    element={<Dashboard />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/campaign-dashboard"
                element={
                  <PrivateRoute
                    element={<CampaignDashboard />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/templates"
                element={
                  <PrivateRoute
                    element={<Template />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/emails"
                element={
                  <PrivateRoute
                    element={<Emails />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/domains"
                element={
                  <PrivateRoute
                    element={<Domains />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/sms/dashboard"
                element={
                  <PrivateRoute
                    element={<SMSDashboard />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/sms/campaign"
                element={
                  <PrivateRoute
                    element={<SMSCampaign />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/sms/contacts"
                element={
                  <PrivateRoute
                    element={<SMSContacts />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/sms/contacts/:groupName"
                element={
                  <PrivateRoute
                    element={<SMSContacts />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/sms/phones"
                element={
                  <PrivateRoute
                    element={<SMSPhones />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/admin"
                element={
                  <PrivateRoute
                    element={<AdminDashboard />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute
                    element={
                      <RoleRoute user={user} allowedRoles={[ROLE_ADMIN]}>
                        <Settings />
                      </RoleRoute>
                    }
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/myprofile"
                element={
                  <PrivateRoute
                    element={<MyProfile />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/billing"
                element={
                  <PrivateRoute
                    element={<Billing />}
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/messages"
                element={
                  <PrivateRoute
                    element={
                      <RoleRoute
                        user={user}
                        allowedRoles={[...STAFF_ROLES]}
                      >
                        <Messages />
                      </RoleRoute>
                    }
                    isAuthenticated={authenticated}
                  />
                }
              />
              <Route
                path="/mobile-monitoring"
                element={
                  <PrivateRoute
                    element={<AdminMobileMonitoring user={user} />}
                    isAuthenticated={authenticated}
                  />
                }
              />
            </Route>

            {/* MailsballEditorPro as standalone page - No Layout */}
            <Route
              path="/templates/editor-pro"
              element={
                <PrivateRoute
                  element={<MailsballEditorPro />}
                  isAuthenticated={authenticated}
                />
              }
            />
            <Route
              path="/templates/editor-pro/:templateId"
              element={
                <PrivateRoute
                  element={<MailsballEditorPro />}
                  isAuthenticated={authenticated}
                />
              }
            />

            <Route path="*" element={<ErrorPage statusCode={404} />} />
          </Routes>
        </SocketProvider>
      </SnackbarProvider>
    </ErrorBoundary>
  );
};

// Экспортируем компонент-обертку
function App() {
  return <AppWithNavigation />;
}

export default App;
